import { computeRateAdequacy, computeRateChange } from "domain/rateAdequacy";
import * as lossDevelopmentStore from "store/lossDevelopment";
import { getPrior } from "store/selectors/common/prior";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import { createSelector } from "store/selectors/stateSelectors";

/*
 * Selectors behind the Rate Adequacy tab.
 *
 * The target loss ratio is, for now, the Initial Expected Loss Ratio entered
 * on Inputs > Loss Development (`lossDevelopment.initialExpectedLossRatio`, a
 * decimal), not the `target_loss_ratios` config schedule shown on that page.
 *
 * The previous-year column runs the same inputs against the linked prior
 * submission (state.temp.priorState.state).
 */

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

// Program expense inputs are entered as whole percentages (10 means 10%).
export const percentToDecimal = (value) =>
  isNumber(value) ? value / 100 : null;

export const selectLossPick = (state) => {
  const value = lossDevelopmentStore.selectUltimateSummary(state)?.lossRatio
    ?.selection?.value;
  return isNumber(value) ? value : null;
};

export const selectTargetLossRatio = (state) => {
  const value = state?.lossDevelopment?.initialExpectedLossRatio;
  return isNumber(value) ? value : null;
};

const getProgram = (state) => state?.input?.program ?? null;

const buildInputs = ({ lossPick, program, targetLossRatio }) => {
  if (program == null) {
    return null;
  }
  return {
    lossPick,
    expectedCommission: percentToDecimal(program.otherAcquisitionCosts),
    brokerage: percentToDecimal(program.brokerage),
    expenses: percentToDecimal(program.expenses),
    targetLossRatio,
  };
};

export const selectRateAdequacyInputs = createSelector(
  [selectLossPick, getProgram, selectTargetLossRatio],
  (lossPick, program, targetLossRatio) =>
    buildInputs({ lossPick, program, targetLossRatio })
);

export const selectPriorRateAdequacyInputs = createSelector(
  [
    getPrior(selectLossPick),
    getPrior(getProgram),
    getPrior(selectTargetLossRatio),
  ],
  (lossPick, program, targetLossRatio) =>
    buildInputs({ lossPick, program, targetLossRatio })
);

const withAdequacy = (inputs) =>
  inputs == null ? null : { ...inputs, ...computeRateAdequacy(inputs) };

export const selectRateAdequacy = createSelector(
  [selectRateAdequacyInputs],
  withAdequacy
);

export const selectPriorRateAdequacy = createSelector(
  [selectPriorRateAdequacyInputs],
  withAdequacy
);

const hasPriorState = (state) => state?.temp?.priorState?.state != null;

export const selectRateAdequacyComparison = createSelector(
  [
    selectRateAdequacy,
    selectPriorRateAdequacy,
    programSelectors.getPriorSubmissionId,
    hasPriorState,
  ],
  (current, prior, priorSubmissionId, priorLoaded) => ({
    current,
    prior: prior ?? null,
    rateChange: computeRateChange(current?.adequacy, prior?.adequacy),
    hasPrior: priorSubmissionId != null || priorLoaded,
  })
);
